import React from 'react';
import { getAllRoles, deleteRole } from '../../actions/RoleManagement/services';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import {
  useMediaQuery,
  IconButton,
  TableRow,
  TableCell,
  Table,
  TableBody,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';

// icons
import AddButton from 'components/MUIDatatableAddButton/AddButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CreateOrUpdateRoleForm from 'components/CreateOrUpdateRoleForm/CreateOrUpdateRoleForm';
import { useSnackbar } from 'notistack';
import ConfirmActionModal from 'components/ConfirmActionModal/ConfirmActionModal';
import MUIDataTableWrapper from 'components/MUIDataTableWrapper/MUIDataTableWrapper';

// Import Zustand store
import { useAppStore } from '../../stores';

interface IRolesManagementProps {}

const RolesManagement: React.FC<IRolesManagementProps> = () => {
  const displayMobile = useMediaQuery('(max-width:959px)');

  const { enqueueSnackbar } = useSnackbar();

  // Zustand store
  const roles = useAppStore((state) => state.rolesAndPermissions.roles) as unknown[];
  const setRoles = useAppStore((state) => state.setRoles);
  const setSelectedRole = useAppStore((state) => state.setSelectedRole);
  const setSelectedUserType = useAppStore((state) => state.setSelectedUserType);

  const [initialRequestPending, setInitialRequestPending] = React.useState(true);

  const [deleteRequestPending, setDeleteRequestPending] = React.useState(false);

  const [displayCreateUpdateFormOnLoad, setDisplayCreateUpdateFormOnLoad] =
    React.useState(false);

  const [roleToBeDeleted, setRoleToBeDeleted] = React.useState(0);

  const onClickRowEdit = (tableMeta: { rowData?: unknown[] } | unknown[]) => {
    let name = '';
    let id = 0;
    if ('rowData' in (tableMeta as object) && (tableMeta as { rowData?: unknown[] }).rowData) {
      const { rowData } = tableMeta as { rowData: unknown[] };
      name = String(rowData[0]);
      id = rowData[1] as number;
    } else {
      const arr = tableMeta as unknown[];
      name = String(arr[0]);
      id = arr[1] as number;
    }
    setSelectedRole({ name: name, id: id });
    setDisplayCreateUpdateFormOnLoad(true);
  };

  const onClickRowDelete = (tableMeta: { rowData?: unknown[] } | unknown[]) => {
    const roleId = 'rowData' in (tableMeta as object)
      ? ((tableMeta as { rowData: unknown[] }).rowData[1] as number)
      : ((tableMeta as unknown[])[1] as number);
    setRoleToBeDeleted(roleId);
    setConfirmModalActionOpen(true);
  };

  const formatDataAndSetInReduxStore = (data: unknown) => {
    const formattedRoles = (data as object[]).map(Object.values);
    setRoles(formattedRoles);
  };

  const deleteSpecifiedRole = () => {
    setDisplayCreateUpdateFormOnLoad(false);
    setConfirmModalActionOpen(false);
    setDeleteRequestPending(true);
    deleteRole(roleToBeDeleted)
      .then((response: { data?: object[] }) => {
        if (response && response.data) {
          const { data } = response;
          formatDataAndSetInReduxStore(data);
          setDeleteRequestPending(false);
          enqueueSnackbar('Role Deleted!', {
            variant: 'success',
          });
        }
        setDeleteRequestPending(false);
      })
      .catch(({ response }: { response?: { data?: string } }) => {
        setDeleteRequestPending(false);
        enqueueSnackbar('The following error occured: ' + response?.data, {
          variant: 'error',
        });
      });
  };

  const [confirmModalActionOpen, setConfirmModalActionOpen] = React.useState(false);

  const addBtnOnClick = () => {
    setSelectedRole({ name: '', id: 0 });
    setSelectedUserType({ permissionLabel: '' });
    setDisplayCreateUpdateFormOnLoad(true);
  };

  React.useEffect(() => {
    setInitialRequestPending(true);
    getAllRoles()
      .then((response: { data?: object[] }) => {
        if (response) {
          const { data } = response;
          if (data) {
            formatDataAndSetInReduxStore(data);
          }
        }
        setInitialRequestPending(false);
      })
      .catch((error) => {
        console.log(error);
        setInitialRequestPending(false);
      });
  }, []);

  return (
    <Grid container size={{ xs: 12 }}>
      <Grid size={{ xs: 12 }}>
        <h2>Role Management</h2>
      </Grid>
      <Grid container size={{ xs: 12 }} className="pt-3">
        <Grid size={{ xs: 12, md: displayCreateUpdateFormOnLoad ? 6 : 12 }} className={`${!displayMobile && displayCreateUpdateFormOnLoad &&'pr-5'}`}>
          {initialRequestPending || deleteRequestPending ? (
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Grid size={{ xs: 12, md: 12 }}>
                <GenericSpinner includeOverlay={false} padding={0} />
              </Grid>
              <Grid size={{ xs: 12, md: 12 }} className="pt-3">
                <span>{`${
                  initialRequestPending ? 'Loading Roles' : 'Deleting Role'
                }...`}</span>
              </Grid>
            </Grid>
          ) : (
            <MUIDataTableWrapper
              title={'Roles'}
              data={roles}
              columns={[
                { name: 'Role' },
                {
                  name: 'RoleId',
                  options: {
                    display: 'excluded' as const,
                  },
                },
                {
                  name: 'Edit/View',
                  options: {
                    display: displayMobile ? ('false' as const) : ('true' as const),
                    customBodyRender: (value: unknown, tableMeta: { rowData?: unknown[] }) => {
                      return (
                        <IconButton
                          aria-label="edit"
                          onClick={() => onClickRowEdit(tableMeta)}
                          className="p-0"
                        >
                          <EditIcon />
                        </IconButton>
                      );
                    },
                  },
                },
                {
                  name: 'Delete',
                  options: {
                    display: displayMobile ? ('false' as const) : ('true' as const),
                    customBodyRender: (value: unknown, tableMeta: { rowData?: unknown[] }) => {
                      const rowData = tableMeta.rowData;
                      if (rowData && !!rowData[2]) {
                        return null;
                      }
                      return (
                        <IconButton
                          aria-label="delete"
                          onClick={() => onClickRowDelete(tableMeta)}
                          className="p-0"
                        >
                          <DeleteIcon />
                        </IconButton>
                      );
                    },
                  },
                },
              ]}
              setExpandableRowContent={(rowData: unknown[]) => {
                return (
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={3}>
                      <Table>
                        <TableBody className="inner-tbody-expandable-row">
                          <TableRow>
                            <TableCell align="left" className="tbl-col-container">
                              <div className="tbl-col-title">Edit</div>
                              <div>
                                <IconButton
                                  aria-label="edit"
                                  onClick={() => onClickRowEdit(rowData)}
                                  className="p-0"
                                >
                                  <EditIcon />
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell align="left" className="tbl-col-container">
                              <div className="tbl-col-title">Delete</div>
                              <div>
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => onClickRowDelete(rowData)}
                                  className="p-0"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                );
              }}
              options={{
                print: false,
                download: false,
                viewColumns: false,
                filter: false,
                rowsPerPageOptions: [5, 10, 15, 25],
                selectableRows: 'none',
                customToolbar: () => {
                  return (
                    <AddButton title={'Add Role'} handleClick={addBtnOnClick} />
                  );
                },
              }}
            />
          )}
        </Grid>
        {displayCreateUpdateFormOnLoad ? (
          <Grid size={{ xs: 12, md: 6 }} className={`${displayMobile && 'pt-5'}`}>
            <CreateOrUpdateRoleForm
              formatDataAndSetInReduxStore={formatDataAndSetInReduxStore}
            />
          </Grid>
        ) : null}
      </Grid>
      <ConfirmActionModal
        prompt={'Are you sure you want to delete this role?'}
        modalOpen={confirmModalActionOpen}
        onClose={() => setConfirmModalActionOpen(false)}
        confirmButtonOnClick={deleteSpecifiedRole}
      />
    </Grid>
  );
};

export default RolesManagement;
